<script>
import axios from "axios";
import Swal from "sweetalert2";

const DEFAULT_CONFIG = {
  audio: { label: "Noise", status: false, bobot: 0 },
  numHumans: { label: "Multiple Faces", status: false, bobot: 0 },
  tabSwitch: { label: "Tab Switch", status: false, bobot: 0 },
  detectMultipleScreens: { label: "Multiple Monitors", status: false, bobot: 0 },
  photosAtRandom: { label: "Random Photos", status: false, bobot: 0 },
  forceFullScreen: { label: "Enforce Full Screen", status: false, bobot: 0 },
};

export default {
  emits: ["stored"],

  data() {
    return {
      isVisible: false,
      isLoading: false,
      idjadwal: null,
      configState: this.buildDefaultConfig(),

      nilai_validitas: 70,
    };
  },

  computed: {
    listConfig() {
      return Object.entries(this.configState).map(([name, value]) => ({
        name,
        label: value.label,
        status: value.status,
        bobot: value.bobot,
      }));
    },

    totalBobot() {
      return Object.values(this.configState).reduce(
        (sum, val) => sum + (Number(val.bobot) || 0),
        0
      );
    },

    progressVariant() {
      if (this.totalBobot >= 100) return "success";
      if (this.totalBobot >= 80) return "warning";
      return "info";
    },

    isOverLimit() {
      return this.totalBobot > 100;
    },
  },

  methods: {
    buildDefaultConfig() {
      return Object.fromEntries(
        Object.entries(DEFAULT_CONFIG).map(([key, val]) => [
          key,
          { ...val },
        ])
      );
    },

    open(jadwalRow) {
      this.idjadwal = jadwalRow.idjadwal;
      this.configState = this.buildDefaultConfig();
      this.nilai_validitas = jadwalRow.nilai_validitas ?? 70;
      this.loadFromJson(jadwalRow.config_proctoring);
      this.isVisible = true;
    },

    close() {
      this.isVisible = false;
    },

    loadFromJson(raw) {
      if (!raw) return;

      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

      Object.keys(this.configState).forEach((key) => {
        if (parsed[key] !== undefined) {
          this.configState[key].status = !!parsed[key].status;
          this.configState[key].bobot = Number(parsed[key].bobot) || 0;
        }
      });
    },

    toggleStatus(name) {
      this.configState[name].status = !this.configState[name].status;
    },

    onBobotInput(name, event) {
      let newVal = Number(event.target.value) || 0;
      if (newVal < 0) newVal = 0;

      const otherTotal = this.totalBobot - (Number(this.configState[name].bobot) || 0);
      const maxAllowed = 100 - otherTotal;

      if (newVal > maxAllowed) {
        newVal = maxAllowed < 0 ? 0 : maxAllowed;
        event.target.value = newVal;
      }

      this.configState[name].bobot = newVal;

      if (newVal > 0) {
        this.configState[name].status = true;
      }
    },

    buildPayload() {
      const config_proctoring = {};
      Object.entries(this.configState).forEach(([key, val]) => {
        config_proctoring[key] = {
          status: val.status,
          bobot: Number(val.bobot) || 0,
        };
      });
      return config_proctoring;
    },

    save() {
      if (this.totalBobot !== 100) {
        Swal.fire(
          "Peringatan",
          `Total bobot harus tepat 100%. Saat ini: ${this.totalBobot}%`,
          "warning"
        );
        return;
      }
      this.isLoading = true;

      axios({
        url:
          process.env.VUE_APP_BACKEND_URL_VERSION +
          `master/jadwal-pelaksanaan/${this.idjadwal}/config-proctoring`,
        method: "put",
        data: { config_proctoring: this.buildPayload(), validitas: this.nilai_validitas },
        headers: { Authorization: `Bearer ${localStorage.access_token}` },
      })
        .then((res) => {
          if (res?.data?.meta?.code == 200) {
            Swal.fire({
              icon: "success",
              title: "Berhasil",
              text: res?.data?.meta?.message || "Config berhasil diperbarui",
              timer: 1500,
              timerProgressBar: true,
              showConfirmButton: false,
            });
            this.close();
            this.$emit("stored");
          } else {
            Swal.fire(
              "Peringatan",
              res?.data?.meta?.message || "Terdapat kendala saat menyimpan",
              "warning"
            );
          }
        })
        .catch((err) => {
          console.log(err);
          const msg =
            err?.response?.data?.meta?.message ||
            "Terjadi kesalahan pada sistem";
          Swal.fire({ icon: "error", title: "Error", text: msg });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<template>
  <b-modal
    v-model="isVisible"
    title="Config Proctor"
    title-class="fw-bold"
    hide-footer
    no-close-on-backdrop
    centered
    size="md"
  >
    <div v-if="isLoading" class="text-center py-4">
      <i class="fas fa-spinner fa-spin fa-2x"></i>
      <p class="mt-2 text-muted">Memuat data...</p>
    </div>

    <template v-else>
      <div class="row mb-3 align-items-center">
        <div class="col-12 col-lg-6 fw-bold">Nilai Validitas</div>

        <div class="col-12 col-lg-6">
          <div class="input-group input-group-sm">
            <input
              type="number"
              class="form-control"
              v-model="nilai_validitas"
              min="0"
              max="100"
              step="1"
              :disabled="isLoading"
            />
            <span class="input-group-text">%</span>
          </div>
        </div>
      </div>

      <div class="mb-3">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <small class="text-muted fw-semibold">Total Bobot</small>
          <small
            class="fw-bold"
            :class="totalBobot === 100 ? 'text-success' : totalBobot > 80 ? 'text-warning' : 'text-secondary'"
          >
            {{ totalBobot }}% / 100%
          </small>
        </div>
        <div class="progress" style="height: 10px; border-radius: 6px;">
          <div
            class="progress-bar"
            :class="`bg-${progressVariant}`"
            role="progressbar"
            :style="{ width: Math.min(totalBobot, 100) + '%', transition: 'width 0.25s ease' }"
          ></div>
        </div>
        <small v-if="totalBobot < 100" class="text-muted">
          Sisa: {{ 100 - totalBobot }}%
        </small>
        <small v-else-if="totalBobot === 100" class="text-success">
          Total bobot sudah 100%
        </small>
      </div>

      <table class="table w-100 align-middle">
        <thead class="bg-light">
          <tr>
            <th>Indikator</th>
            <th style="width: 130px">Bobot (%)</th>
            <th style="width: 80px">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in listConfig" :key="item.name">
            <td>{{ item.label }}</td>
            <td>
              <div class="input-group input-group-sm">
                <input
                  type="number"
                  class="form-control"
                  :value="item.bobot"
                  min="0"
                  max="100"
                  step="1"
                  :disabled="isLoading"
                  @input="onBobotInput(item.name, $event)"
                />
                <span class="input-group-text">%</span>
              </div>
            </td>
            <td>
              <label class="mySwitch mb-0">
                <input
                  type="checkbox"
                  :name="item.name"
                  :checked="item.status"
                  @change="toggleStatus(item.name)"
                />
                <span class="mySlider"></span>
              </label>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="d-flex justify-content-end gap-2 mt-3">
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isLoading"
          @click="close"
        >
          Batal
        </b-button>
        <b-button
          type="button"
          variant="primary"
          size="sm"
          :disabled="isLoading"
          @click="save"
        >
          <i class="fa fa-save me-1"></i> Simpan
        </b-button>
      </div>
    </template>
  </b-modal>
</template>
